<script lang="ts">
  import { onMount } from 'svelte';
  import AppShell from '../components/AppShell.svelte';
  import Icon from '../components/Icon.svelte';
  import FieldTypeIcon from '../components/FieldTypeIcon.svelte';
  import StatusBadge from '../components/StatusBadge.svelte';
  import Avatar from '../components/Avatar.svelte';
  import {
    fetchPersonDetail,
    fetchPersonPayments,
    type PersonDetail,
    type PersonPaymentRow,
  } from '../lib/persons-api';
  import EntityTimeline from '../components/EntityTimeline.svelte';
  import FunnelJourney from '../components/FunnelJourney.svelte';
  import InlineEdit from '../components/InlineEdit.svelte';
  import TagsInput from '../components/TagsInput.svelte';
  import { fmtDate, fmtInt, fmtRelative, fmtUah } from '../lib/format';
  import { fieldType, type FieldTypeKey } from '../lib/data-tables-catalog';
  import { addIdentity, promoteIdentity, removeIdentity } from '../lib/persons-api';
  import { updatePerson } from '../lib/admin-api';
  import { fetchPublicDataTable, type ApiField, type ApiFieldGroup } from '../lib/data-tables-api';
  import { session } from '../lib/session';
  import InlineFieldEdit from '../lib/fields/InlineFieldEdit.svelte';
  import ReverseLinks from '../lib/fields/ReverseLinks.svelte';
  import { canViewField, type Role } from '../lib/roles';

  let { params }: { params: { id?: string } } = $props();
  const personId = $derived(params?.id ?? '');

  let detail = $state<PersonDetail | null>(null);
  let loading = $state(true);
  let err = $state<string | null>(null);
  let errorMsg = $state<string | null>(null);

  /** Усі field-definitions з catalog'у (built-in + custom) — для unified
   *  schema-driven рендеру у sidebar (sections derived нижче). */
  let allFields = $state<ApiField[]>([]);
  /** Custom-groups з catalog (Settings → Структура даних → users → групи). */
  let allGroups = $state<ApiFieldGroup[]>([]);

  /**
   * Default group для built-in field якщо у admin override-row немає groupName.
   * Coverage: усі built-in поля Person. Якщо field не у мапі — потрапляє у "Додаткові".
   * Admin може перевизначити через Settings → Структура даних → drag&drop між групами.
   */
  const DEFAULT_GROUPS: Record<string, string> = {
    displayName: 'Контакти',
    username: 'Контакти',
    primaryEmail: 'Контакти',
    primaryPhone: 'Контакти',
    telegramId: 'Контакти',
    sendpulseContactId: 'Контакти',
    currentTariff: 'Підписка',
    currentSubscriptionStatus: 'Підписка',
    subscriptionEndsAt: 'Підписка',
    ltvCents: 'Фінанси',
    paymentCount: 'Фінанси',
    payments: 'Фінанси',
    firstSeenAt: 'Час',
    lastSeenAt: 'Час',
  };
  /** Bias order для built-in default групп — вони не існують як CustomFieldGroup
   *  записи, тому свою позицію беруть з цього preset'а. Admin-створені групи
   *  ідуть після, у порядку CustomFieldGroup.position. */
  const SECTION_ORDER = ['Контакти', 'Підписка', 'Фінанси', 'Час'];

  /** Identity-aware fields що потребують спец UX (add/promote/remove identity). */
  const IDENTITY_FIELDS: Record<string, { kind: string; placeholder: string; mono?: boolean }> = {
    primaryEmail: { kind: 'EMAIL', placeholder: 'name@example.com' },
    primaryPhone: { kind: 'PHONE', placeholder: '+380...' },
    telegramId: { kind: 'TELEGRAM_ID', placeholder: '609822579', mono: true },
    sendpulseContactId: { kind: 'SENDPULSE_CONTACT_ID', placeholder: '6739a1b2c…', mono: true },
  };

  /** Згруповані поля для рендеру у sidebar. Враховує:
   *  - hidden (через override) → виключаються
   *  - groupName override → визначає секцію
   *  - DEFAULT_GROUPS для built-in без override
   *  - position у межах групи
   *  - SECTION_ORDER + CustomFieldGroup.position для порядку секцій */
  const currentRole = $derived(($session?.role ?? null) as Role | null);

  const sections = $derived.by<{ groupName: string; fields: ApiField[] }[]>(() => {
    if (allFields.length === 0) return [];
    const map = new Map<string, ApiField[]>();
    for (const f of allFields) {
      if (f.hidden) continue;
      if (!canViewField(currentRole, f.min_view_role)) continue;
      let groupName: string;
      if (f.groupName) groupName = f.groupName;
      else if (f.builtIn) groupName = DEFAULT_GROUPS[f.fieldKey] ?? 'Додаткові';
      else groupName = 'Додаткові';
      const arr = map.get(groupName) ?? [];
      arr.push(f);
      map.set(groupName, arr);
    }
    // Sort fields within each group by position.
    for (const [, arr] of map) {
      arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    // Section order: preset built-in defaults first, then custom groups by CustomFieldGroup position.
    const result: { groupName: string; fields: ApiField[] }[] = [];
    for (const name of SECTION_ORDER) {
      const arr = map.get(name);
      if (arr && arr.length > 0) {
        result.push({ groupName: name, fields: arr });
        map.delete(name);
      }
    }
    const customGroupPos = new Map<string, number>();
    for (const g of allGroups) customGroupPos.set(g.name, g.position);
    const customSorted = Array.from(map.entries()).sort(([a], [b]) => {
      const pa = customGroupPos.get(a) ?? 9999;
      const pb = customGroupPos.get(b) ?? 9999;
      if (pa !== pb) return pa - pb;
      return a.localeCompare(b);
    });
    for (const [groupName, fields] of customSorted) {
      result.push({ groupName, fields });
    }
    return result;
  });
  function fieldOf(key: string): ApiField | null {
    return allFields.find((f) => f.fieldKey === key) ?? null;
  }

  /** Активна вкладка у правій колонці. Default 'timeline', але можна deep-link
   *  з list-view: #/persons/abc?tab=payments → скролить до Фінанси-блоку. */
  function readTabFromHash(): 'timeline' | 'analytics' | null {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx < 0) return null;
    const v = new URLSearchParams(hash.slice(qIdx + 1)).get('tab');
    return v === 'analytics' ? 'analytics' : null;
  }
  let activeTab = $state<'timeline' | 'analytics'>(readTabFromHash() ?? 'timeline');
  /** Linked payments — завантажуються разом з detail (показуються у Фінанси sidebar). */
  let personPayments = $state<PersonPaymentRow[]>([]);
  let personPaymentsTotal = $state(0);

  async function refresh() {
    if (!personId) return;
    try {
      detail = await fetchPersonDetail(personId);
      errorMsg = null;
    } catch (e) {
      errorMsg = e instanceof Error ? e.message : String(e);
    }
  }

  onMount(async () => {
    if (!personId) {
      err = 'Не вказано id';
      loading = false;
      return;
    }
    try {
      const [d, schema] = await Promise.all([
        fetchPersonDetail(personId),
        fetchPublicDataTable('users').catch(() => null),
      ]);
      detail = d;
      // Payments — окремо, з видимою помилкою (а не silent swallow). Endpoint може
      // бути недоступний якщо api-server не рестартований після додавання маршруту.
      try {
        const pays = await fetchPersonPayments(personId);
        personPayments = pays.items;
        personPaymentsTotal = pays.total;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error('[PersonDetail] fetchPersonPayments failed:', e);
        errorMsg = `Платежі не завантажились: ${msg} — рестартуйте api-сервер після останніх змін.`;
      }
      // Усі схема-поля для sections derived (sidebar). hidden фільтруються
      // всередині, default-groups застосовуються для built-in без override.
      allFields = schema?.fields ?? [];
      allGroups = schema?.groups ?? [];
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
    // Deep-link з Persons list: #/persons/abc?tab=payments → скролимо до Фінанси
    // секції (де тепер живе список платежів). Виконуємо після рендеру, тож setTimeout.
    if (window.location.hash.includes('tab=payments')) {
      setTimeout(() => {
        document.getElementById('finance-section')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  });

  /** Telegram ID з identities (бо це не колонка Person'а). */
  function telegramId(d: PersonDetail): string | null {
    const tg = d.identities.find((i) => i.kind === 'TELEGRAM_ID');
    return tg?.value ?? null;
  }

  const isAdmin = $derived($session?.role === 'ADMIN');

  /** Універсальний save для Person.* column'ів (працює з InlineEdit.onSave). */
  async function savePersonField(
    field: 'displayName' | 'username' | 'currentTariff' | 'currentSubscriptionStatus' | 'subscriptionEndsAt',
    next: string | null,
  ): Promise<void> {
    const body: Record<string, string | null> = {};
    if (field === 'subscriptionEndsAt') {
      // InlineEdit type='date' → YYYY-MM-DD; конвертуємо у ISO datetime
      body[field] = next ? new Date(next).toISOString() : null;
    } else {
      body[field] = next;
    }
    await updatePerson(personId, body);
    await refresh();
  }

  /** Schema-driven save: приймає typed value з InlineFieldEdit (string/null
   *  для більшості built-in Person columns; DateTimePicker дає ISO string). */
  async function savePersonFieldTyped(key: string, next: unknown): Promise<void> {
    const v = next === '' || next === undefined ? null : next;
    await updatePerson(personId, { [key]: v } as Record<string, string | null>);
    await refresh();
  }

  /** Збереження одного custom-поля (jsonb merge на бекенді). */
  async function saveCustomField(fieldKey: string, next: string | null): Promise<void> {
    await updatePerson(personId, { customFields: { [fieldKey]: next } });
    await refresh();
  }

  /** Schema-driven save для будь-якого custom field type (currency, dropdown, tags…).
   *  Передає типізоване value у customFields jsonb merge.
   *  null/'' = очистити ключ (backend видаляє з jsonb). */
  async function saveTypedCustomField(fieldKey: string, next: unknown): Promise<void> {
    const v = next === '' || next === undefined ? null : next;
    await updatePerson(personId, { customFields: { [fieldKey]: v } });
    await refresh();
  }

  // Identity-керування (інтегроване у секцію Контакти).
  const newIdent = $state<Record<string, string>>({});
  let identBusy = $state<Record<string, boolean>>({});

  async function onAddIdent(kind: string) {
    const v = (newIdent[kind] ?? '').trim();
    if (!v) return;
    identBusy[kind] = true;
    try {
      await addIdentity(personId, kind, v);
      newIdent[kind] = '';
      await refresh();
    } catch (e) {
      errorMsg = e instanceof Error ? e.message : String(e);
    } finally {
      identBusy[kind] = false;
    }
  }
  async function onPromoteIdent(id: string) {
    try {
      await promoteIdentity(personId, id);
      await refresh();
    } catch (e) {
      errorMsg = e instanceof Error ? e.message : String(e);
    }
  }
  async function onRemoveIdent(id: string, value: string) {
    if (!confirm(`Видалити ${value}?`)) return;
    try {
      await removeIdentity(personId, id);
      await refresh();
    } catch (e) {
      errorMsg = e instanceof Error ? e.message : String(e);
    }
  }
</script>

<AppShell active="persons">
  <div class="mx-auto max-w-[1280px]">
    <div class="text-xs text-[var(--color-text-muted)] mb-2">
      <a href="#/persons" class="text-[var(--color-pu)] no-underline hover:underline">Користувачі</a> ›
      <span>{detail?.displayName ?? personId.slice(0, 8)}</span>
    </div>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else if detail}
      <!-- Header card -->
      <div class="mb-5 flex items-start gap-4 rounded-xl border border-[var(--color-border-3)] bg-gradient-to-b from-[var(--color-bg-3)] to-white p-4 sm:p-5">
        <div class="hidden sm:block shrink-0">
          <Avatar name={detail.displayName} size={64} />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2.5 mb-1 flex-wrap">
            <span class="text-lg font-bold">{detail.displayName ?? '— без імені —'}</span>
            <StatusBadge status={detail.currentSubscriptionStatus} />
            <code class="rounded bg-[var(--color-bg-2)] px-1.5 py-0.5 text-[10.5px] text-[var(--color-text-muted)]">p-{detail.id.slice(0, 8)}</code>
          </div>
          <div class="flex flex-wrap gap-x-6 gap-y-2 pt-1">
            <div>
              <div class="text-[9px] uppercase tracking-[1.2px] text-[var(--color-text-muted-2)] font-semibold mb-1">LTV</div>
              <div class="text-lg font-bold">{fmtUah(detail.ltvCents)}</div>
            </div>
            <div>
              <div class="text-[9px] uppercase tracking-[1.2px] text-[var(--color-text-muted-2)] font-semibold mb-1">Платежів</div>
              <div class="text-lg font-bold">{fmtInt(detail.paymentCount)}</div>
            </div>
            <div>
              <div class="text-[9px] uppercase tracking-[1.2px] text-[var(--color-text-muted-2)] font-semibold mb-1">Тариф</div>
              <div class="text-sm font-bold pt-1">{detail.currentTariff ?? '—'}</div>
            </div>
            <div>
              <div class="text-[9px] uppercase tracking-[1.2px] text-[var(--color-text-muted-2)] font-semibold mb-1">До</div>
              <div class="text-sm font-bold pt-1">{detail.subscriptionEndsAt ? fmtDate(detail.subscriptionEndsAt) : '—'}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Body: 2 columns (fields | tabs) -->
      <div class="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 items-start">
        <!-- LEFT: Деталі (поля Person'a у стилі data-tables-catalog) -->
        <aside class="rounded-xl border border-[var(--color-border-3)] bg-white">
          <div class="px-4 py-3 border-b border-[var(--color-border-3)] flex items-center justify-between">
            <span class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Деталі</span>
            <a
              href="#/settings/data-structure/tables/users"
              class="text-[10px] text-[var(--color-pu-3)] hover:underline"
              title="Налаштувати поля"
            >налаштувати</a>
          </div>

          <!-- Unified schema-driven renderer: усі секції генеруються з catalog'у
               (Settings → Структура даних → users), respect-ить groupName + position.
               Admin перенесе LTV у нову групу — секція переміститься тут автоматично. -->
          {#each sections as section, sIdx (section.groupName)}
            <div
              class="px-4 py-3 {sIdx === 0 ? '' : 'border-t border-[var(--color-border-3)]'}"
              id={section.groupName === 'Фінанси' ? 'finance-section' : undefined}
            >
              <div class="text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)] mb-2">
                {section.groupName}
              </div>
              {#each section.fields as f (f.id)}
                {@const idIdent = IDENTITY_FIELDS[f.fieldKey]}
                {#if idIdent}
                  <!-- Identity-aware: add/promote/remove inline. Bespoke UX. -->
                  {@render IdentityGroup({
                    kind: idIdent.kind,
                    label: f.label,
                    iconType: fieldType(f.type).key,
                    placeholder: idIdent.placeholder,
                    mono: idIdent.mono ?? false,
                  })}
                {:else if f.fieldKey === 'payments'}
                  <!-- linked_record: список платежів як клікабельні плашки.
                       Header: іконка ↗ + label з catalog + count. Consistent з ReadOnlyRow. -->
                  <div class="flex items-start gap-2.5 rounded-md px-2 py-1.5 mb-1">
                    <span class="grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--color-text-muted-2)] mt-0.5" title="Пов'язані записи"><Icon name="externalLink" size={13} /></span>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-1.5">
                        <span class="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">{f.label}</span>
                        <span class="text-[10.5px] text-[var(--color-text-muted)] tabular-nums">{personPaymentsTotal}</span>
                      </div>
                    </div>
                  </div>
                  {#if personPayments.length === 0 && detail.paymentCount > 0}
                    <div class="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[10.5px] text-amber-800">
                      Лічильник показує <strong>{detail.paymentCount}</strong> платежів, але список порожній. Можливо api-сервер не перезавантажений — або платежі прив'язані до іншого Person (merge).
                    </div>
                  {:else if personPayments.length === 0}
                    <div class="text-[10.5px] text-[var(--color-text-muted-2)] italic">Немає платежів</div>
                  {:else}
                    <div class="space-y-1.5">
                      {#each personPayments as pay (pay.id)}
                        <a
                          href={`#/payments/${pay.id}`}
                          class="block rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-2 no-underline hover:border-[var(--color-pu-2)] hover:bg-[var(--color-bg-3)] transition-colors"
                          title="Відкрити платіж"
                        >
                          <div class="flex items-center justify-between gap-2 mb-0.5">
                            <span class="font-bold tabular-nums text-[12.5px] {pay.status === 'REFUNDED' ? 'text-red-600' : 'text-[var(--color-text)]'}">
                              {pay.status === 'REFUNDED' ? '– ' : ''}{fmtUah(pay.amountCents)}
                            </span>
                            <StatusBadge status={pay.status} />
                          </div>
                          <div class="flex items-center justify-between gap-2 text-[10.5px] text-[var(--color-text-muted)]">
                            <span class="truncate">
                              {#if pay.productName}{pay.productName}
                              {:else if pay.productSku}<span class="font-mono">{pay.productSku}</span>
                              {:else if pay.rawTariff}<span class="font-mono">{pay.rawTariff}</span>
                              {:else}—{/if}
                            </span>
                            <span class="whitespace-nowrap tabular-nums">{fmtDate(pay.occurredAt)}</span>
                          </div>
                          {#if pay.inferred}
                            <div class="mt-0.5 text-[9.5px]">
                              <span class="inline-block rounded-full bg-blue-100 px-1.5 py-0.5 font-bold text-blue-700">inferred</span>
                            </div>
                          {/if}
                        </a>
                      {/each}
                    </div>
                  {/if}
                {:else if f.fieldKey === 'ltvCents'}
                  {@render ReadOnlyRow({ iconType: fieldType(f.type).key, label: f.label, value: fmtUah(detail.ltvCents) })}
                {:else if f.fieldKey === 'paymentCount'}
                  {@render ReadOnlyRow({ iconType: fieldType(f.type).key, label: f.label, value: fmtInt(detail.paymentCount) })}
                {:else if f.fieldKey === 'firstSeenAt'}
                  {@render ReadOnlyRow({ iconType: fieldType(f.type).key, label: f.label, value: fmtDate(detail.firstSeenAt) })}
                {:else if f.fieldKey === 'lastSeenAt'}
                  {@render ReadOnlyRow({ iconType: fieldType(f.type).key, label: f.label, value: fmtRelative(detail.lastSeenAt) })}
                {:else if f.builtIn && f.fieldKey === 'displayName'}
                  <!-- Bespoke: завжди показуємо (protected), використовуємо EditableRow
                       для консистентного inline-edit UX (а не InlineFieldEdit pencil). -->
                  {@render EditableRow({ iconType: fieldType(f.type).key, label: f.label, value: detail.displayName, onSave: (v) => savePersonField('displayName', v) })}
                {:else if f.builtIn && f.fieldKey === 'username'}
                  {@render EditableRow({ iconType: fieldType(f.type).key, label: f.label, value: detail.username, onSave: (v) => savePersonField('username', v) })}
                {:else if f.builtIn}
                  <!-- Editable built-in field — value з detail.<fieldKey>, save через PATCH. -->
                  <InlineFieldEdit
                    field={f}
                    value={(detail as unknown as Record<string, unknown>)[f.fieldKey]}
                    onSave={(v) => savePersonFieldTyped(f.fieldKey, v)}
                    readOnly={!isAdmin}
                    record={{ ...(detail as unknown as Record<string, unknown>), ...(detail.customFields ?? {}) }}
                  />
                {:else}
                  <!-- Custom field — value з customFields jsonb. -->
                  <InlineFieldEdit
                    field={f}
                    value={detail.customFields?.[f.fieldKey]}
                    onSave={(v) => saveTypedCustomField(f.fieldKey, v)}
                    readOnly={!isAdmin}
                    record={{ ...(detail as unknown as Record<string, unknown>), ...(detail.customFields ?? {}) }}
                  />
                {/if}
              {/each}
            </div>
          {/each}

          <!-- Зворотні зв'язки — окрема група знизу полів (хто посилається на цей контакт). -->
          <div class="px-4 py-3 border-t border-[var(--color-border-3)]">
            <ReverseLinks sourceSlug="users" sourceId={detail.id} />
          </div>

          {#if errorMsg}
            <div class="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[10.5px] text-red-700">{errorMsg}</div>
          {/if}
        </aside>

        <!-- RIGHT: Tabs (Timeline / Аналітика). Платежі живуть у sidebar
             блоку "Фінанси" як список клікабельних плашок. -->
        <main class="min-w-0">
          <div class="mb-4 border-b border-[var(--color-border-3)]">
            <div class="flex gap-1">
              <button
                onclick={() => (activeTab = 'timeline')}
                class="pd-tab inline-flex items-center gap-1.5"
                class:active={activeTab === 'timeline'}
              >
                <Icon name="chartLine" size={14} /> Timeline
              </button>
              <button
                onclick={() => (activeTab = 'analytics')}
                class="pd-tab inline-flex items-center gap-1.5"
                class:active={activeTab === 'analytics'}
              >
                <Icon name="chartBars" size={14} /> Аналітика
              </button>
            </div>
          </div>

          {#if activeTab === 'timeline'}
            <!-- Unified activity log (P3): events + field-changes + payments +
                 automation runs + funnel transitions + relations, for CONTACT. -->
            <EntityTimeline entityType="CONTACT" entityId={personId} />
          {:else if activeTab === 'analytics'}
            <!-- Зворотні зв'язки перенесено у колонку полів (окрема група знизу). -->
            <!-- Funnel Journey (P5): reached/current/skipped/future per funnel. -->
            <div class="mb-4">
              <FunnelJourney entityType="CONTACT" entityId={personId} />
            </div>

            <!-- Активність counters -->
            <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4 mb-4">
              <div class="inline-flex items-center gap-1.5 text-[13px] font-bold mb-3"><Icon name="chartBars" size={15} /> Активність</div>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div class="rounded-md bg-[var(--color-bg-3)] px-3 py-2.5">
                  <div class="text-[9.5px] uppercase tracking-wider font-bold text-[var(--color-text-muted-2)]">Платежів</div>
                  <div class="text-lg font-bold mt-0.5">{fmtInt(detail.paymentsCount)}</div>
                </div>
                <div class="rounded-md bg-[var(--color-bg-3)] px-3 py-2.5">
                  <div class="text-[9.5px] uppercase tracking-wider font-bold text-[var(--color-text-muted-2)]">Підписок</div>
                  <div class="text-lg font-bold mt-0.5">{fmtInt(detail.subscriptionsCount)}</div>
                </div>
                <div class="rounded-md bg-[var(--color-bg-3)] px-3 py-2.5">
                  <div class="text-[9.5px] uppercase tracking-wider font-bold text-[var(--color-text-muted-2)]">Матеріалів</div>
                  <div class="text-lg font-bold mt-0.5">{fmtInt(detail.materialsCount)}</div>
                </div>
                <div class="rounded-md bg-[var(--color-bg-3)] px-3 py-2.5">
                  <div class="text-[9.5px] uppercase tracking-wider font-bold text-[var(--color-text-muted-2)]">В обраному</div>
                  <div class="text-lg font-bold mt-0.5">{fmtInt(detail.favoritesCount)}</div>
                </div>
              </div>
            </div>
          {/if}
        </main>
      </div>
    {/if}
  </div>
</AppShell>

{#snippet ReadOnlyRow({ iconType, label, value }: { iconType: FieldTypeKey; label: string; value: string | null | undefined })}
  <div class="flex items-start gap-2.5 rounded-md px-2 py-1.5 mb-0.5">
    <span class="grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--color-text-muted-2)] mt-0.5"><FieldTypeIcon type={iconType} size={13} /></span>
    <div class="flex-1 min-w-0">
      <div class="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">{label}</div>
      <div class="text-[12.5px] {value ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted-2)] italic'} truncate">
        {value ?? '—'}
      </div>
    </div>
  </div>
{/snippet}

{#snippet EditableRow({ iconType, label, value, onSave, type = 'text', options, mono = false }: {
  iconType: FieldTypeKey;
  label: string;
  value: string | null | undefined;
  onSave: (next: string | null) => Promise<void>;
  type?: 'text' | 'date' | 'datetime' | 'select' | 'tags';
  options?: { value: string; label: string }[];
  mono?: boolean;
})}
  <div class="flex items-start gap-2.5 rounded-md px-2 py-1.5 mb-0.5">
    <span class="grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--color-text-muted-2)] mt-0.5"><FieldTypeIcon type={iconType} size={13} /></span>
    <div class="flex-1 min-w-0">
      <div class="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">{label}</div>
      {#if type === 'tags'}
        <TagsInput value={value ?? null} onSave={onSave} readOnly={!isAdmin} />
      {:else}
        <InlineEdit
          value={value}
          onSave={onSave}
          readOnly={!isAdmin}
          type={type}
          options={options}
          mono={mono}
        />
      {/if}
    </div>
  </div>
{/snippet}

{#snippet IdentityGroup({ kind, label, iconType, placeholder, mono = false }: {
  kind: string; label: string; iconType: FieldTypeKey; placeholder: string; mono?: boolean;
})}
  {@const items = detail?.identitiesByKind[kind] ?? []}
  <div class="rounded-md px-2 py-1.5 mb-0.5">
    <div class="flex items-center gap-2.5">
      <span class="grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--color-text-muted-2)]"><FieldTypeIcon type={iconType} size={13} /></span>
      <div class="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">{label}</div>
    </div>
    {#if items.length === 0}
      <div class="ml-7 text-[11.5px] text-[var(--color-text-muted-2)] italic">—</div>
    {:else}
      <div class="ml-7 space-y-0.5">
        {#each items as ident (ident.id)}
          <div class="group flex items-center gap-1 rounded px-1 py-0.5 text-[12px]" class:bg-purple-50={ident.isPrimary}>
            {#if ident.isPrimary}
              <span class="inline-flex items-center rounded-full bg-purple-200 px-1 py-0.5 text-purple-800"><Icon name="star" size={10} /></span>
            {/if}
            <span class="flex-1 truncate {mono ? 'font-mono text-[11px]' : ''}" class:font-bold={ident.isPrimary}>{ident.value}</span>
            {#if isAdmin}
              <div class="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {#if !ident.isPrimary}
                  <button onclick={() => onPromoteIdent(ident.id)} title="Зробити основним" class="inline-flex items-center rounded px-1 text-[var(--color-pu-3)] hover:bg-purple-100"><Icon name="star" size={12} /></button>
                {/if}
                <button onclick={() => onRemoveIdent(ident.id, ident.value)} title="Видалити" class="inline-flex items-center rounded px-1 text-red-600 hover:bg-red-100"><Icon name="close" size={12} /></button>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
    {#if isAdmin}
      <form onsubmit={(e) => { e.preventDefault(); void onAddIdent(kind); }} class="ml-7 mt-1 flex gap-1">
        <input
          bind:value={newIdent[kind]}
          placeholder={placeholder}
          disabled={identBusy[kind]}
          class="flex-1 rounded border border-[var(--color-border-3)] px-1.5 py-0.5 text-[10.5px] {mono ? 'font-mono' : ''}"
        />
        <button
          type="submit"
          disabled={identBusy[kind] || !(newIdent[kind] ?? '').trim()}
          class="rounded bg-[var(--color-pu)] px-1.5 py-0.5 text-[10px] font-semibold text-white disabled:opacity-40"
        >+</button>
      </form>
    {/if}
  </div>
{/snippet}

<style>
  :global(.pd-tab) {
    padding: 8px 16px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--color-text-muted);
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    background: none;
    cursor: pointer;
  }
  :global(.pd-tab:hover) {
    color: var(--color-text);
  }
  :global(.pd-tab.active) {
    color: var(--color-pu-3);
    border-bottom-color: var(--color-pu-3);
  }
</style>
